'use client';

import { startTransition, useOptimistic, useState } from 'react';

import { usePatchJobDeliverableConfidenceMutation } from '@/features/jobs/api/jobsApi';
import { Badge, Popover } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './JobTasksComponents.module.scss';

import type { JobDeliverable, ProgressConfidence } from '@/features/jobs/types';

type DeliverableConfidenceControlProps = {
  jobId?: string;
  deliverableId: string;
  value: ProgressConfidence | undefined;
  readOnly: boolean;
  onPatched?: (patch: Partial<JobDeliverable>) => void;
};

const CONFIDENCE_OPTIONS: Array<{
  value: ProgressConfidence;
  label: string;
  tone: 'success' | 'warning' | 'error' | 'default';
}> = [
  { value: 'high', label: 'High', tone: 'success' },
  { value: 'medium', label: 'Medium', tone: 'warning' },
  { value: 'low', label: 'Low', tone: 'error' },
  { value: null, label: 'Not set', tone: 'default' },
];

function getConfidenceOption(confidence: ProgressConfidence | undefined) {
  return (
    CONFIDENCE_OPTIONS.find(
      (option) => option.value === (confidence ?? null),
    ) ?? CONFIDENCE_OPTIONS[3]
  );
}

export function DeliverableConfidenceControl({
  jobId,
  deliverableId,
  value,
  readOnly,
  onPatched,
}: DeliverableConfidenceControlProps) {
  const [patchConfidence, { isLoading }] =
    usePatchJobDeliverableConfidenceMutation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [committedConfidence, setCommittedConfidence] =
    useState<ProgressConfidence>(value ?? null);
  const [optimisticConfidence, setOptimisticConfidence] =
    useOptimistic(committedConfidence);

  const confidenceOption = getConfidenceOption(optimisticConfidence);
  const isBusy = readOnly || isLoading;

  const handleConfidenceChange = async (next: ProgressConfidence) => {
    const previousConfidence = optimisticConfidence;

    if (next === previousConfidence) {
      setAnchorEl(null);
      return;
    }

    startTransition(() => {
      setOptimisticConfidence(next);
    });
    setCommittedConfidence(next);
    setAnchorEl(null);

    try {
      const response = await patchConfidence({
        ...(jobId ? { jobId } : {}),
        deliverableId,
        payload: {
          progress_confidence: next,
        },
      }).unwrap();
      onPatched?.(response.data ?? { progress_confidence: next });
    } catch (error) {
      startTransition(() => {
        setOptimisticConfidence(previousConfidence);
      });
      setCommittedConfidence(previousConfidence);
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <span className={styles.metricCard}>
      <button
        type="button"
        aria-label={`Update deliverable confidence: ${confidenceOption.label}`}
        className={styles.inlineMetaButton}
        disabled={isBusy}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Badge
          label={confidenceOption.label}
          tone={confidenceOption.tone}
          size="lg"
        />
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <div className={styles.inlineMetaMenu} role="menu">
          {CONFIDENCE_OPTIONS.map((option) => (
            <button
              key={option.value ?? 'not-set'}
              type="button"
              role="menuitem"
              className="action-item"
              disabled={isBusy}
              onClick={() => {
                void handleConfidenceChange(option.value);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Popover>
    </span>
  );
}
